import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Search, FileText, Download, Eye, DollarSign, User, Calendar, X } from 'lucide-react';
import { payrollAPI } from '../../../services/api';
import { toast } from 'sonner';

const PayrollResults = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRun, setSelectedRun] = useState('');
  const [selectedPayslip, setSelectedPayslip] = useState(null);


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [resultsRes, runsRes] = await Promise.all([
        payrollAPI.getResults(selectedRun ? { payroll_run_id: selectedRun } : {}),
        payrollAPI.getRuns()
      ]);
      setResults(resultsRes.data || []);
      setRuns(runsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load payroll results');
    } finally {
      setLoading(false);
    }
  }, [selectedRun]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredResults = results.filter(result => {
    const matchesSearch = 
      (result.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (result.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleViewPayslip = async (resultId) => {
    try {
      const response = await payrollAPI.getPayslip(resultId);
      setSelectedPayslip(response.data);
    } catch (error) {
      toast.error('Failed to load payslip');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'CALCULATED': 'bg-orange-100 text-orange-700',
      'VERIFIED': 'bg-green-100 text-green-700',
      'ERROR': 'bg-red-100 text-red-700',
      'ADJUSTED': 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="payroll-results-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/payroll')}>Payroll</span>
            <span>/</span>
            <span>Results</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Results</h1>
          <p className="text-gray-600 mt-1">View employee payroll calculations and payslips</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <User className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Employees</p>
                <p className="text-xl font-semibold">{results.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Gross</p>
                <p className="text-xl font-semibold">{formatCurrency(results.reduce((sum, r) => sum + (r.gross_earnings || 0), 0))}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Tax</p>
                <p className="text-xl font-semibold">{formatCurrency(results.reduce((sum, r) => sum + (r.income_tax || 0), 0))}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Net</p>
                <p className="text-xl font-semibold">{formatCurrency(results.reduce((sum, r) => sum + (r.net_pay || 0), 0))}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={selectedRun}
            onChange={(e) => setSelectedRun(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg min-w-[200px]"
          >
            <option value="">All Payroll Runs</option>
            {runs.map(run => (
              <option key={run.id} value={run.id}>
                {run.run_number} - {run.period_year}/{run.period_number}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredResults.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No payroll results found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employee</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Gross</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Tax</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">SS</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Net Pay</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredResults.map((result) => (
                    <tr key={result.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{result.employee_name || result.employee_id}</p>
                          <p className="text-sm text-gray-500">{result.employee_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {result.period_year}/{result.period_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                        {formatCurrency(result.gross_earnings)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">
                        {formatCurrency(result.income_tax)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-orange-600">
                        {formatCurrency(result.social_security_employee)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-green-600">
                        {formatCurrency(result.net_pay)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(result.status)}
                      </td>
                      <td className="px-4 py-3">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleViewPayslip(result.id)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Payslip
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Payslip Modal */}
        {selectedPayslip && (
          <PayslipModal 
            payslip={selectedPayslip} 
            onClose={() => setSelectedPayslip(null)} 
          />
        )}
      </div>
    </Layout>
  );
};

// Payslip Modal Component
const PayslipModal = ({ payslip, onClose }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{payslip.payslip_title || 'PAYSLIP'}</h2>
            <p className="text-gray-600">{payslip.company_name}</p>
            {payslip.company_address && <p className="text-sm text-gray-500">{payslip.company_address}</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Employee Info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Employee Name</p>
              <p className="font-medium">{payslip.employee_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Employee ID</p>
              <p className="font-medium">{payslip.employee_id}</p>
            </div>
            {payslip.designation && (
              <div>
                <p className="text-sm text-gray-500">Designation</p>
                <p className="font-medium">{payslip.designation}</p>
              </div>
            )}
            {payslip.department && (
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium">{payslip.department}</p>
              </div>
            )}
          </div>

          {/* Period Info */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            Pay Period: {payslip.period_description} ({payslip.period_start} to {payslip.period_end})
          </div>

          {/* Earnings */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Earnings
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-green-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-green-800">Description</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-green-800">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payslip.earnings?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm">{item.wage_type_name}</td>
                      <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-green-50 font-semibold">
                    <td className="px-4 py-2 text-sm">Total Earnings</td>
                    <td className="px-4 py-2 text-sm text-right text-green-700">{formatCurrency(payslip.total_earnings)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Deductions */}
          {payslip.deductions?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Deductions</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-orange-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-orange-800">Description</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-orange-800">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payslip.deductions?.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm">{item.wage_type_name}</td>
                        <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-orange-50 font-semibold">
                      <td className="px-4 py-2 text-sm">Total Deductions</td>
                      <td className="px-4 py-2 text-sm text-right text-orange-700">{formatCurrency(payslip.total_deductions)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Taxes */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Taxes & Contributions</h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-red-800">Description</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-red-800">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payslip.taxes?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm">{item.wage_type_name}</td>
                      <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-red-50 font-semibold">
                    <td className="px-4 py-2 text-sm">Total Taxes</td>
                    <td className="px-4 py-2 text-sm text-right text-red-700">{formatCurrency(payslip.total_taxes)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Net Pay */}
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-orange-900">NET PAY</span>
              <span className="text-2xl font-bold text-orange-900">{formatCurrency(payslip.net_pay)}</span>
            </div>
            {payslip.net_pay_words && (
              <p className="text-sm text-orange-700 mt-1 italic">{payslip.net_pay_words}</p>
            )}
          </div>

          {/* Bank Details */}
          {payslip.bank_name && (
            <div className="text-sm text-gray-600">
              <p>Payment to: {payslip.bank_name} - A/c: {payslip.account_number_masked}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayrollResults;
